import React, { useState } from "react";
import isEmail from "validator/lib/isEmail";
import isMobilePhone from "validator/lib/isMobilePhone";
import axios from "axios";
import Button from "../Button/Button";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import Text from "../Text/Text";
import FormControl from "../FormControl/FormControl";
import FormLabel from "../FormControl/FormLabel/FormLabel";
import Input from "../Input/Input";
import styles from "./Form.module.scss";
import FormErrorCaption from "../FormControl/FormErrorCaption/FormErrorCaption";
import Checkbox from "../Checkbox/Checkbox";
import Icon from "../../assets/icons/Icon";

const DEFAULT_FIELDS = {
  company: "",
  email: "",
  name: "",
  phone: "",
  title: "",
};

/** For submitting details to receive a full DMA report */
const Form = React.forwardRef((props, ref) => {
  const { scores, ranking, handleModal } = props;
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [fields, setFields] = useState({
    ...DEFAULT_FIELDS,
    ranking: ranking.toString(),
    scores: scores.map((s) => ({
      label: s.label,
      score: s.score,
    })),
  });
  const [errors, setErrors] = useState({
    ...DEFAULT_FIELDS,
    privacy: "",
  });

  const handleField = (e, field) => {
    setFields((prevFields) => ({
      ...prevFields,
      [field]: e.target.value,
    }));

    if (errors[field] !== "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formSubmitted) return;
    let formValid = true;

    if (fields.name.trim() === "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        name: "Your name is required.",
      }));
      formValid = false;
    }

    if (fields.email.trim() === "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Your email is required.",
      }));
      formValid = false;
    } else if (!isEmail(fields.email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Not a valid email.",
      }));
      formValid = false;
    }

    if (fields.phone.trim() === "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phone: "Your phone number is required.",
      }));
      formValid = false;
    } else if (!isMobilePhone(fields.phone)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phone: "Not a valid phone number.",
      }));
      formValid = false;
    }

    if (fields.company.trim() === "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        company: "Your company name is required.",
      }));
      formValid = false;
    }

    if (fields.title.trim() === "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        title: "Your job title is required.",
      }));
      formValid = false;
    }

    if (!privacyPolicy) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        privacy: "You must agree to the privacy policy.",
      }));
      formValid = false;
    }

    if (formValid) {
      setFormLoading(true);
      try {
        const response = await axios.post(
          process.env.REACT_APP_LOGIC_APP_URL,
          fields
        );
        if (response.status === 200) {
          setFormLoading(false);
          setFormSubmitted(true);
        }
        if (response.status === 200) {
          setFormLoading(false);
          setFormSubmitted(true);
        } else {
          setFormError(true);
        }
      } catch (error) {
        setFormError(error);
      }
    }
  };

  return (
    <div className={styles.form_wrapper}>
      <form ref={ref} className={styles.form}>
        <div className={styles.form_body}>
          {formSubmitted ? (
            <FormSuccess />
          ) : (
            <>
              <Text
                variant="subheading"
                weight="bold"
                className={styles.form_title}
              >
                <div className={styles.form_title_icon_container}>
                  <Icon className={styles.form_title_icon} icon="GtCheckmark" />
                </div>
                Get in touch to schedule your full Digital Maturity Assessment
                today
              </Text>
              <FormFields
                fields={fields}
                errors={errors}
                handleField={handleField}
                setPrivacyPolicy={setPrivacyPolicy}
                privacyPolicy={privacyPolicy}
              />
            </>
          )}

          {formError ? (
            <Text variant="bodyRegular" className={styles.form_error}>
              Something went wrong. Please try again. If the problem persists,
              please reach out directly to{" "}
              <a href="mailto:hesus.inoma@ie.gt.com">Hesus Inoma</a>.
            </Text>
          ) : null}
        </div>
        <div className={styles.form_footer}>
          {formSubmitted ? (
            <Button onClick={() => handleModal(false)} size="md">
              Close
            </Button>
          ) : (
            <ButtonGroup
              isDisabled={formSubmitted}
              variant={["ghost", "filled"]}
              size="md"
              justify="end"
              className={styles.footer_group}
            >
              <Button onClick={() => handleModal(false)}>Cancel</Button>
              <Button
                isLoading={formLoading}
                loadingText="Submitting..."
                type="submit"
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </ButtonGroup>
          )}
        </div>
      </form>
    </div>
  );
});

function FormSuccess() {
  return (
    <div className={styles.form_success}>
      <Text className={styles.form_success_message}>
        Thank you. We will be in touch shortly.
      </Text>
    </div>
  );
}

function FormFields(props) {
  const { errors, fields, handleField, privacyPolicy, setPrivacyPolicy } =
    props;
  return (
    <div className={styles.form_fields}>
      <FormControl
        id="name"
        name="name"
        isRequired
        isInvalid={!!errors.name}
        size="sm"
        className={styles.form_control}
      >
        <FormLabel>First name</FormLabel>
        <Input value={fields.name} onChange={(e) => handleField(e, "name")} />
        <FormErrorCaption>{errors.name}</FormErrorCaption>
      </FormControl>
      <div className={styles.form_group}>
        <FormControl
          id="email"
          name="email"
          isRequired
          isInvalid={!!errors.email}
          size="sm"
          className={styles.form_control}
        >
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            value={fields.email}
            onChange={(e) => handleField(e, "email")}
          />
          <FormErrorCaption>{errors.email}</FormErrorCaption>
        </FormControl>
        <FormControl
          name="phone"
          id="phone"
          isRequired
          isInvalid={!!errors.phone}
          size="sm"
          className={styles.form_control}
        >
          <FormLabel>Phone number</FormLabel>
          <Input
            type="tel"
            value={fields.phone}
            onChange={(e) => handleField(e, "phone")}
          />
          <FormErrorCaption>{errors.phone}</FormErrorCaption>
        </FormControl>
      </div>
      <div className={styles.form_group}>
        <FormControl
          id="company"
          name="company"
          isRequired
          isInvalid={!!errors.company}
          size="sm"
          className={styles.form_control}
        >
          <FormLabel>Company name</FormLabel>
          <Input
            value={fields.company}
            onChange={(e) => handleField(e, "company")}
          />
          <FormErrorCaption>{errors.company}</FormErrorCaption>
        </FormControl>
        <FormControl
          id="title"
          name="title"
          isRequired
          isInvalid={!!errors.title}
          size="sm"
          className={styles.form_control}
        >
          <FormLabel>Job title</FormLabel>
          <Input
            value={fields.title}
            onChange={(e) => handleField(e, "title")}
          />
          <FormErrorCaption>{errors.title}</FormErrorCaption>
        </FormControl>
      </div>
      <div className={styles.privacy}>
        <Checkbox
          isInvalid={!!errors.privacy}
          isChecked={privacyPolicy}
          onChange={() => setPrivacyPolicy((prev) => !prev)}
          className={styles.privacy_checkbox}
        >
          I agree with the privacy policy
        </Checkbox>
        {errors.privacy && (
          <Text variant="caption" className={styles.privacy_error}>
            You must agree to the privacy policy.
          </Text>
        )}
      </div>
    </div>
  );
}

export default Form;
